import React, { useState } from "react";
import Markdown from "./Markdown";
import useEscapeKey from "./useEscapeKey";

// 日報エディタ（Markdown 入力＋ライブプレビュー）。退勤時の日報入力と、退勤後の再編集で共用する。
// PC は「書く／プレビュー」の2ペイン、スマホはタブ切替（狭い画面で2ペインは窮屈なため）。
// プレビューは実際の表示と同じ自作 Markdown.js を使う＝「書いた通りに見える」ことを確認できる。
//
// props:
//   open         表示可否
//   title        見出し（例「退勤して日報を提出」/「日報を編集」）
//   initialText  初期本文（再編集時は既存の日報、退勤時は空）
//   submitLabel  確定ボタン文言（例「退勤する」/「保存する」）
//   busy         送信中（多重送信防止でボタンを無効化）
//   onCancel     閉じる
//   onSubmit     確定（trim 済みの本文を渡す）
//
// 空の日報は退勤・保存とも不可（退勤=日報必須の仕様。再編集でも空で上書きさせない）。

const MAX_LEN = 5000; // BE の MAX_DAILY_REPORT_LEN と一致させる（超過は BE でも 400）

export default function ReportEditor({ open, title, initialText = "", submitLabel = "提出する", busy = false, onCancel, onSubmit }) {
  // モーダルを開くたびに初期本文へ戻す（前回の入力が残らないよう key で再マウントする前提。
  // 呼び出し側は open の切替時に key を変えて新規インスタンスにする）。
  const [text, setText] = useState(initialText);
  const [tab, setTab] = useState("write"); // スマホのタブ（write | preview）。PC は CSS で常に両方表示。

  useEscapeKey(onCancel, open);
  if (!open) return null;

  const trimmed = text.trim();
  const over = text.length > MAX_LEN;
  const canSubmit = trimmed.length > 0 && !over && !busy;

  const submit = () => { if (canSubmit) onSubmit(trimmed); };

  return (
    <div className="mp-modal-back" onClick={onCancel}>
      <div className="mp-modal mp-editor" role="dialog" aria-modal="true" aria-label={title} onClick={(e) => e.stopPropagation()}>
        <div className="mp-ed-head">
          <h3>{title}</h3>
          {/* スマホのみ表示（PCは2ペイン同時表示なのでタブ不要＝CSSで隠す）。 */}
          <div className="mp-tabs mp-ed-tabs">
            <button type="button" className={`mp-tab${tab === "write" ? " mp-on" : ""}`} onClick={() => setTab("write")}>書く</button>
            <button type="button" className={`mp-tab${tab === "preview" ? " mp-on" : ""}`} onClick={() => setTab("preview")}>プレビュー</button>
          </div>
        </div>

        <div className="mp-ed-panes">
          {/* 入力ペイン */}
          <div className={`mp-ed-pane mp-ed-write${tab === "write" ? " mp-on" : ""}`}>
            <div className="mp-ed-label">入力</div>
            <textarea
              className="mp-textarea mp-ed-ta"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={"本日の業務内容を記入してください。\n\n見出しは # 、箇条書きは - （字下げで入れ子）、\nチェックは - [ ] 、太字は **文字** で書けます。"}
              autoFocus
            />
          </div>
          {/* プレビュー ペイン（実際の日報表示と同じレンダラ）。空のうちは案内文。
              ラベルは入力側と揃えて枠の外に置き、本文だけを枠内に入れる。 */}
          <div className={`mp-ed-pane mp-ed-preview${tab === "preview" ? " mp-on" : ""}`}>
            <div className="mp-ed-label">プレビュー</div>
            <div className="mp-ed-pv-box">
              {trimmed ? <Markdown text={text} /> : <div className="mp-ed-pv-empty">ここに書いた内容のプレビューが表示されます。</div>}
            </div>
          </div>
        </div>

        <div className="mp-ed-foot">
          <span className={`mp-ed-count${over ? " mp-over" : ""}`}>{text.length} / {MAX_LEN}</span>
          <div className="mp-ed-actions">
            <button type="button" className="mp-btn mp-ghost" onClick={onCancel} disabled={busy}>やめる</button>
            <button type="button" className="mp-btn mp-primary" onClick={submit} disabled={!canSubmit}>{busy ? "送信中…" : submitLabel}</button>
          </div>
        </div>
      </div>
    </div>
  );
}
